<template>
    <div :class="`signature-align-${localAlign} signature`">
        <label for="signature" class="field-label" @click="focusFieldLabel" v-if="!editingFieldLabel">
            {{ displayFieldLabel }}
            <span v-if="isRequired" class="required-indicator">*</span>
        </label>
        <input
            v-if="editingFieldLabel"
            type="text"
            class="field-label-input"
            placeholder="İmza"
            v-model="localFieldLabel"
            @blur="stopEditingFieldLabel"
            @keydown.enter="stopEditingFieldLabel"
            ref="fieldLabelInput"
        />
        <div class="signature-container">
            <div class="signature-canvas-wrapper" :style="canvasWrapperStyle">
                <canvas
                    ref="signatureCanvas"
                    :width="canvasWidth"
                    :height="canvasHeight"
                    :style="canvasStyle"
                    @mousedown="startDrawing"
                    @mousemove="draw"
                    @mouseup="stopDrawing"
                    @mouseleave="stopDrawing"
                    @touchstart="startDrawing"
                    @touchmove="draw"
                    @touchend="stopDrawing"
                ></canvas>
                <div v-if="isEmpty && !isDrawing" class="signature-placeholder">
                    {{ placeholder || "Burada imza çizin" }}
                </div>
                <button
                    v-if="showClearButton && !isEmpty"
                    @click="clearSignature"
                    class="clear-button"
                    type="button"
                >
                    {{ clearButtonText || "Temizle" }}
                </button>
            </div>
            <div v-if="altLabel" class="alt-label">{{ altLabel }}</div>
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
    name: "FormSignature",
    props: {
        fieldLabel: String,
        align: String,
        altLabel: String,
        isRequired: Boolean,
        canvasWidth: {
            type: Number,
            default: 400,
        },
        canvasHeight: {
            type: Number,
            default: 200,
        },
        strokeColor: {
            type: String,
            default: "#000000",
        },
        strokeWidth: {
            type: Number,
            default: 2,
        },
        backgroundColor: {
            type: String,
            default: "#ffffff",
        },
        borderColor: {
            type: String,
            default: "#e5e7eb",
        },
        borderWidth: {
            type: Number,
            default: 1,
        },
        borderRadius: {
            type: Number,
            default: 8,
        },
        showClearButton: {
            type: Boolean,
            default: true,
        },
        clearButtonText: {
            type: String,
            default: "Temizle",
        },
        placeholder: {
            type: String,
            default: "Burada imza çizin",
        },
        item: Object,
    },
    data() {
        return {
            localFieldLabel: this.fieldLabel || "İmza",
            localAlign: this.align || "top",
            editingFieldLabel: false,
            isDrawing: false,
            isEmpty: true,
            lastX: 0,
            lastY: 0,
            ctx: null,
        };
    },
    computed: {
        displayFieldLabel() {
            return this.localFieldLabel || "İmza";
        },
        canvasWrapperStyle() {
            return {
                width: `${this.canvasWidth}px`,
                height: `${this.canvasHeight}px`,
                backgroundColor: this.backgroundColor,
                borderColor: this.borderColor,
                borderWidth: `${this.borderWidth}px`,
                borderRadius: `${this.borderRadius}px`,
            };
        },
        canvasStyle() {
            return {
                cursor: "crosshair",
                borderRadius: `${this.borderRadius}px`,
            };
        },
    },
    mounted() {
        this.initCanvas();
        console.log("🔴 Signature mounted:", {
            fieldLabel: this.fieldLabel,
            align: this.align,
            canvasWidth: this.canvasWidth,
            canvasHeight: this.canvasHeight,
            strokeColor: this.strokeColor,
            strokeWidth: this.strokeWidth,
        });
    },
    methods: {
        ...mapActions({
            updateFormField: "forms/updateFormField",
        }),

        initCanvas() {
            const canvas = this.$refs.signatureCanvas;
            if (!canvas) return;

            this.ctx = canvas.getContext("2d");
            this.ctx.lineCap = "round";
            this.ctx.lineJoin = "round";
            this.ctx.strokeStyle = this.strokeColor;
            this.ctx.lineWidth = this.strokeWidth;

            this.ctx.fillStyle = this.backgroundColor;
            this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
        },

        focusFieldLabel() {
            this.editingFieldLabel = true;
            this.$nextTick(() => {
                if (this.$refs.fieldLabelInput) {
                    this.$refs.fieldLabelInput.focus();
                }
            });
        },

        stopEditingFieldLabel() {
            this.editingFieldLabel = false;
            this.updateField();
        },

        getEventPos(e) {
            const canvas = this.$refs.signatureCanvas;
            const rect = canvas.getBoundingClientRect();
            const clientX = e.clientX || (e.touches && e.touches[0].clientX);
            const clientY = e.clientY || (e.touches && e.touches[0].clientY);

            return {
                x: clientX - rect.left,
                y: clientY - rect.top,
            };
        },

        startDrawing(e) {
            e.preventDefault();
            this.isDrawing = true;
            this.isEmpty = false;

            const pos = this.getEventPos(e);
            this.lastX = pos.x;
            this.lastY = pos.y;
        },

        draw(e) {
            if (!this.isDrawing) return;
            e.preventDefault();

            const pos = this.getEventPos(e);

            this.ctx.beginPath();
            this.ctx.moveTo(this.lastX, this.lastY);
            this.ctx.lineTo(pos.x, pos.y);
            this.ctx.stroke();

            this.lastX = pos.x;
            this.lastY = pos.y;
        },

        stopDrawing(e) {
            if (!this.isDrawing) return;
            e.preventDefault();

            this.isDrawing = false;
            this.saveSignature();
        },

        clearSignature() {
            if (!this.ctx) return;

            this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
            this.ctx.fillStyle = this.backgroundColor;
            this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

            this.isEmpty = true;
            this.saveSignature();
        },

        saveSignature() {
            const canvas = this.$refs.signatureCanvas;
            if (canvas) {
                const signatureData = canvas.toDataURL("image/png");
                console.log("🔴 Signature saved:", signatureData.substring(0, 50) + "...");
            }
        },

        updateField() {
            const fieldData = {
                fieldLabel: this.localFieldLabel,
                align: this.localAlign,
            };

            this.$emit("update-field", fieldData);
            console.log("🔴 Signature field updated:", fieldData);
        },
    },
    watch: {
        fieldLabel(newVal) {
            this.localFieldLabel = newVal || "İmza";
        },
        align(newVal) {
            this.localAlign = newVal || "top";
        },
        strokeColor(newVal) {
            if (this.ctx) {
                this.ctx.strokeStyle = newVal;
            }
        },
        strokeWidth(newVal) {
            if (this.ctx) {
                this.ctx.lineWidth = newVal;
            }
        },
        backgroundColor(newVal) {
            if (this.ctx) {
                this.ctx.fillStyle = newVal;
                this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
            }
        },
        canvasWidth() {
            this.$nextTick(() => {
                this.initCanvas();
            });
        },
        canvasHeight() {
            this.$nextTick(() => {
                this.initCanvas();
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.signature {
    width: 100%;
    max-width: 500px;
    margin: clamp(0.75rem, 2vw, 1.25rem) auto;
    padding: clamp(0.75rem, 2vw, 1rem);
    position: relative;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    .field-label {
        display: block;
        font-size: clamp(0.875rem, 1.8vw, 1rem);
        font-weight: 600;
        color: rgba(55, 65, 81, 0.9);
        margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        cursor: pointer;
        transition: color 0.2s ease;

        &:hover {
            color: #f26a52;
        }

        .required-indicator {
            color: #ef4444;
            margin-left: 0.25rem;
            font-weight: 700;
        }
    }

    .field-label-input {
        padding: clamp(0.5rem, 1.2vw, 0.75rem);
        font-size: clamp(0.875rem, 1.8vw, 1rem);
        font-weight: 600;
        color: rgba(55, 65, 81, 0.9);
        background: rgba(255, 255, 255, 0.8);
        border: 2px solid #f26a52;
        border-radius: 0.5rem;
        margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        transition: all 0.2s ease;

        &:focus {
            outline: none;
            background: rgba(255, 255, 255, 0.95);
            box-shadow: 0 0 0 3px rgba(242, 106, 82, 0.1);
        }
    }

    .signature-container {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .signature-canvas-wrapper {
        position: relative;
        border: solid;
        border-style: solid;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        transition: all 0.3s ease;

        &:hover {
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        canvas {
            display: block;
            touch-action: none;
            user-select: none;
        }
    }

    .signature-placeholder {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: rgba(107, 114, 128, 0.6);
        font-size: clamp(0.875rem, 1.8vw, 1rem);
        font-weight: 500;
        pointer-events: none;
        text-align: center;
        z-index: 1;
    }

    .clear-button {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        padding: clamp(0.375rem, 1vw, 0.5rem) clamp(0.75rem, 1.5vw, 1rem);
        font-size: clamp(0.75rem, 1.5vw, 0.875rem);
        font-weight: 600;
        color: #ffffff;
        background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        border: none;
        border-radius: 0.375rem;
        cursor: pointer;
        transition: all 0.2s ease;
        z-index: 2;

        &:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
        }

        &:active {
            transform: translateY(0);
        }
    }

    .alt-label {
        font-size: clamp(0.75rem, 1.5vw, 0.875rem);
        color: rgba(107, 114, 128, 0.7);
        text-align: center;
        margin-top: clamp(0.25rem, 0.8vw, 0.5rem);
    }
}

.signature-align-top {
    .field-label {
        text-align: left;
    }
}

.signature-align-left {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    .field-label {
        flex-shrink: 0;
        margin-bottom: 0;
        margin-top: clamp(0.5rem, 1.2vw, 0.75rem);
    }

    .signature-container {
        flex: 1;
    }
}

.signature-align-center {
    .field-label {
        text-align: center;
    }
}

.signature-align-right {
    .field-label {
        text-align: right;
    }
}

@media (max-width: 768px) {
    .signature {
        margin: 0.5rem auto;
        padding: 0.75rem;

        .signature-canvas-wrapper {
            max-width: 100%;

            canvas {
                max-width: 100%;
                height: auto;
            }
        }

        .clear-button {
            padding: 0.375rem 0.75rem;
            font-size: 0.75rem;
        }
    }

    .signature-align-left {
        flex-direction: column;

        .field-label {
            margin-bottom: 0.5rem;
            margin-top: 0;
        }
    }
}

@media (max-width: 480px) {
    .signature {
        margin: 0.25rem auto;
        padding: 0.5rem;

        .signature-canvas-wrapper {
            width: 100% !important;
            min-height: 150px;

            canvas {
                width: 100% !important;
                max-width: 100% !important;
            }
        }
    }
}

.signature {
    will-change: transform;

    .signature-canvas-wrapper {
        will-change: box-shadow;
    }

    canvas {
        will-change: auto;
    }
}


@media (prefers-reduced-motion: reduce) {
    .signature {
        transition: none;

        &:hover {
            transform: none;
        }
    }

    .clear-button {
        transition: none;

        &:hover {
            transform: none;
        }
    }
}
</style>
